import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeftRight, Copy, Download, Share2, History, AlertTriangle, ChevronDown, ChevronUp, Calculator } from 'lucide-react';

// Unit conversion factors (to square feet as base)
const unitFactors = {
  'Square Feet': 1,
  'Square Meter': 10.7639,
  'Square Yard': 9,
  'Gaj': 9,
  'Acre': 43560,
  'Hectare': 107639,
  'Dismil': 435.6,
  'Bigha (Chhattisgarh)': 12000,
  'Katha (Chhattisgarh)': 1200,
  'Bigha (Bihar)': 27220,
  'Katha (Bihar)': 1361.25,
};

const panIndiaUnits = ['Square Feet', 'Square Meter', 'Square Yard', 'Gaj', 'Acre', 'Hectare', 'Dismil'];
const chhattisgarhUnits = ['Bigha (Chhattisgarh)', 'Katha (Chhattisgarh)'];
const biharUnits = ['Bigha (Bihar)', 'Katha (Bihar)'];

const regions = ['Pan India', 'Chhattisgarh', 'Bihar'];

export default function LandConverterPage() {
  const [amount, setAmount] = useState('1000');
  const [fromUnit, setFromUnit] = useState('Square Feet');
  const [toUnits, setToUnits] = useState(['Square Meter', 'Square Yard', 'Acre']);
  const [region, setRegion] = useState('Pan India');
  const [precision, setPrecision] = useState(4);
  const [history, setHistory] = useState([]);
  const [showFormulas, setShowFormulas] = useState({});

  const availableUnits = useMemo(() => {
    let units = [...panIndiaUnits];
    if (region === 'Chhattisgarh') {
      units = [...units, ...chhattisgarhUnits];
    } else if (region === 'Bihar') {
      units = [...units, ...biharUnits];
    }
    return units;
  }, [region]);

  const results = useMemo(() => {
    const numAmount = parseFloat(amount) || 0;
    const baseValue = numAmount * unitFactors[fromUnit]; // Convert to sq ft
    
    return toUnits.map(unit => ({
      unit,
      value: baseValue / unitFactors[unit],
      formula: `${numAmount} ${fromUnit} × ${unitFactors[fromUnit]} ÷ ${unitFactors[unit]} = ${(baseValue / unitFactors[unit]).toFixed(precision)} ${unit}`
    }));
  }, [amount, fromUnit, toUnits, precision]);

  const handleReverse = () => {
    if (toUnits.length === 1) {
      const temp = fromUnit;
      setFromUnit(toUnits[0]);
      setToUnits([temp]);
    }
  };

  const addToHistory = () => {
    const entry = {
      id: Date.now(),
      amount,
      fromUnit,
      results: results.map(r => ({ unit: r.unit, value: r.value.toFixed(precision) })),
      timestamp: new Date().toLocaleString()
    };
    setHistory(prev => [entry, ...prev.slice(0, 9)]);
    toast.success('Added to history');
  };

  const copyResult = (result) => {
    navigator.clipboard.writeText(`${result.value.toFixed(precision)} ${result.unit}`);
    toast.success('Copied to clipboard');
  };

  const copyAllResults = () => {
    const text = results.map(r => `${r.value.toFixed(precision)} ${r.unit}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success('All results copied');
  };

  const exportCSV = () => {
    const headers = ['From Amount', 'From Unit', 'To Amount', 'To Unit'];
    const rows = results.map(r => [amount, fromUnit, r.value.toFixed(precision), r.unit]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'land-conversion.csv';
    a.click();
    toast.success('CSV exported');
  };

  const shareLink = () => {
    const params = new URLSearchParams({
      amount,
      from: fromUnit,
      to: toUnits.join(','),
      region
    });
    const url = `${window.location.origin}/land-size-converter?${params.toString()}`;
    navigator.clipboard.writeText(url);
    toast.success('Shareable link copied');
  };

  const toggleFormula = (unit) => {
    setShowFormulas(prev => ({ ...prev, [unit]: !prev[unit] }));
  };

  const handleToUnitChange = (unit) => {
    if (toUnits.includes(unit)) {
      if (toUnits.length > 1) {
        setToUnits(toUnits.filter(u => u !== unit));
      }
    } else {
      setToUnits([...toUnits, unit]);
    }
  };

  const isRegionalUnit = (unit) => {
    return chhattisgarhUnits.includes(unit) || biharUnits.includes(unit);
  };

  return (
    <div className="pt-16 lg:pt-20" data-testid="land-converter-page">
      {/* Header */}
      <section className="bg-gradient-to-r from-brand-rust to-brand-orange py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center text-white">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Calculator className="h-8 w-8" />
            <h1 className="font-heading text-4xl lg:text-5xl font-bold">
              Land Size Converter
            </h1>
          </div>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            Convert between different land measurement units including regional units for Chhattisgarh and Bihar
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Converter Card */}
            <div className="lg:col-span-2">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <ArrowLeftRight className="h-5 w-5 text-brand-orange" />
                    <span>Convert Land Area</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Region Selection */}
                  <div className="space-y-2">
                    <Label>State / Region</Label>
                    <Select value={region} onValueChange={setRegion}>
                      <SelectTrigger data-testid="region-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {region === 'Pan India' && (
                      <p className="text-xs text-gray-500">Select a specific state for regional units like Bigha/Katha</p>
                    )}
                  </div>

                  {/* From Input */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Enter amount"
                        className="text-lg"
                        data-testid="amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>From Unit</Label>
                      <Select value={fromUnit} onValueChange={setFromUnit}>
                        <SelectTrigger data-testid="from-unit-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableUnits.map(u => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Reverse Button */}
                  {toUnits.length === 1 && (
                    <div className="flex justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReverse}
                        className="rounded-full"
                        data-testid="reverse-btn"
                      >
                        <ArrowLeftRight className="h-4 w-4 mr-2" />
                        Reverse
                      </Button>
                    </div>
                  )}

                  {/* To Units Selection */}
                  <div className="space-y-2">
                    <Label>Convert To (select multiple)</Label>
                    <div className="flex flex-wrap gap-2">
                      {availableUnits.filter(u => u !== fromUnit).map(unit => (
                        <Button
                          key={unit}
                          variant={toUnits.includes(unit) ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleToUnitChange(unit)}
                          className={`rounded-full ${toUnits.includes(unit) ? 'bg-brand-orange hover:bg-brand-rust' : ''}`}
                          data-testid={`to-unit-${unit.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          {unit}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Regional Warning */}
                  {(fromUnit && isRegionalUnit(fromUnit)) || toUnits.some(isRegionalUnit) ? (
                    region === 'Pan India' && (
                      <div className="flex items-start space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                        <p className="text-sm text-yellow-800">
                          Regional units like Bigha and Katha vary by state. Please select your state for accurate conversion.
                        </p>
                      </div>
                    )
                  ) : null}

                  {/* Precision Selector */}
                  <div className="flex items-center space-x-4">
                    <Label>Decimal Precision:</Label>
                    <Select value={precision.toString()} onValueChange={(v) => setPrecision(parseInt(v))}>
                      <SelectTrigger className="w-20" data-testid="precision-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2, 3, 4, 5, 6].map(p => (
                          <SelectItem key={p} value={p.toString()}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Results */}
              <div className="mt-8 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-xl font-semibold text-gray-900">Results</h2>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={copyAllResults} data-testid="copy-all-btn">
                      <Copy className="h-4 w-4 mr-1" />
                      Copy All
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={shareLink} data-testid="share-btn">
                      <Share2 className="h-4 w-4 mr-1" />
                      Share
                    </Button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {results.map((result) => (
                    <Card key={result.unit} className="calc-result-card">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-gray-500">{result.unit}</p>
                            <p className="font-heading text-2xl font-bold text-gradient">
                              {result.value.toFixed(precision)}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => copyResult(result)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        <button
                          onClick={() => toggleFormula(result.unit)}
                          className="flex items-center text-xs text-gray-500 hover:text-brand-orange mt-2"
                        >
                          {showFormulas[result.unit] ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                          Show formula
                        </button>
                        {showFormulas[result.unit] && (
                          <p className="text-xs text-gray-600 mt-2 bg-gray-50 p-2 rounded font-mono">
                            {result.formula}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Button 
                  onClick={addToHistory}
                  variant="outline"
                  className="w-full mt-4"
                  data-testid="add-to-history-btn"
                >
                  <History className="h-4 w-4 mr-2" />
                  Add to History
                </Button>
              </div>
            </div>

            {/* History Sidebar */}
            <div>
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <History className="h-5 w-5 text-brand-orange" />
                    <span>Conversion History</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {history.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-8">
                      No conversions saved yet
                    </p>
                  ) : (
                    <div className="space-y-4 max-h-[400px] overflow-y-auto">
                      {history.map((entry) => (
                        <div key={entry.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                          <p className="font-medium">{entry.amount} {entry.fromUnit}</p>
                          <div className="text-gray-600 mt-1">
                            {entry.results.map(r => (
                              <p key={r.unit}>= {r.value} {r.unit}</p>
                            ))}
                          </div>
                          <p className="text-xs text-gray-400 mt-2">{entry.timestamp}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6 text-center">
            Common Land Measurement Units
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Pan India Units</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Square Feet (sq ft)</li>
                  <li>• Square Meter (sq m)</li>
                  <li>• Square Yard / Gaj</li>
                  <li>• Acre & Hectare</li>
                  <li>• Dismil</li>
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Chhattisgarh Units</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• 1 Bigha = 12,000 sq ft</li>
                  <li>• 1 Katha = 1,200 sq ft</li>
                  <li>• 10 Katha = 1 Bigha</li>
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Bihar Units</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• 1 Bigha = 27,220 sq ft</li>
                  <li>• 1 Katha = 1,361.25 sq ft</li>
                  <li>• 20 Katha = 1 Bigha</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
